import { useCallback, useEffect, useState } from "react";
import { Basket, Sparkle, Truck, Timer, Storefront, CheckCircle, Plus, MapPin, Tag, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

const Row = ({ label, value, id, green }) => (
  <div className="flex items-baseline justify-between gap-3" data-testid={`pantry-row-${id}`}>
    <dt className="text-slate-600">{label}</dt>
    <dd className={green ? "font-semibold text-[#0B5C2E]" : "font-semibold text-[#0F172A]"}>{value}</dd>
  </div>
);

export default function Pantry() {
  const [data, setData] = useState(null);
  const [wanted, setWanted] = useState("atta flour 10kg\nmustard oil 1 litre\nokra\ntoor dal 2kg");
  const [sourced, setSourced] = useState(null);
  const [basket, setBasket] = useState([]);
  const [address, setAddress] = useState("");
  const [pay, setPay] = useState("online");
  const [runs, setRuns] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [place, setPlace] = useState("");
  const [found, setFound] = useState(null);
  const [zoneKey, setZoneKey] = useState("");
  const [miles, setMiles] = useState(6);
  const [tip, setTip] = useState(0);
  const [receipt, setReceipt] = useState(null);
  const [rates, setRates] = useState(null);
  const [offer, setOffer] = useState({ store: "", title: "", discount_pct: 10 });

  const load = useCallback(() => {
    api.get("/pantry").then(({ data: d }) => {
      setData(d);
      setZoneKey((z) => z || d.my_zone || d.zones?.[0]?.key || "");
      setPlace((p) => p || d.my_place || "");
    }).catch(oops);
    api.get("/pantry/runs").then(({ data: d }) => setRuns(d.runs || [])).catch(() => setRuns([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const zone = (data?.zones || []).find((z) => z.key === zoneKey) || data?.zones?.[0];
  const sym = receipt?.symbol || zone?.symbol || "$";

  const locate = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/pantry/locate", { place, save: !!data?.can_manage });
      setFound(d);
      if (d.zone?.key) setZoneKey(d.zone.key);
      if (d.hub?.miles_from_homes) setMiles(Number(d.hub.miles_from_homes));
      toast.success(`${d.country} — charged in ${(d.zone?.currency || "usd").toUpperCase()}`);
      load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const requote = useCallback(async (lines, tipNow, milesNow, zoneNow) => {
    if (!lines.length) { setReceipt(null); return; }
    try {
      const { data: d } = await api.post("/pantry/quote", {
        lines, zone_key: zoneNow, miles: Number(milesNow) || 0, tip: Number(tipNow) || 0,
        deliver: true,
      });
      setReceipt(d);
    } catch (e) { oops(e); }
  }, []);

  useEffect(() => {
    if (basket.length) requote(basket, tip, miles, zoneKey);
  }, [basket, tip, miles, zoneKey, requote]);

  const source = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/pantry/source", { items: wanted.split("\n"), area: place || "North Woods" });
      setSourced(d);
      setBasket(d.items.map((i) => ({
        item: i.item, category: i.category, pack: i.pack,
        store: (i.options?.[0]?.store) || "", price: Number(i.options?.[0]?.price || 0), qty: 1,
      })));
      toast.success(`${d.engine_label} found it all`);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const order = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/pantry/orders", {
        hub_id: data.hub_id, lines: basket, pay, deliver: true, address,
        zone_key: zoneKey, miles: Number(miles) || 0, tip: Number(tip) || 0,
      });
      toast.success(d.all_in_hub ? `In the hub now — ${d.eta}` : `Booked — ${d.eta}`);
      if (pay === "online") {
        const { data: p } = await api.post("/payments/pantry/checkout",
          { order_id: d.id, origin_url: window.location.origin });
        if (p.url) window.location.href = p.url;
      }
      setSourced(null); setBasket([]); setReceipt(null); setTip(0); load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Loading the pantry…</p>;

  const fmt = (n) => `${sym}${Number(n || 0).toFixed(2)}`;
  const ch = receipt?.charges;

  return (
    <div className="space-y-6" data-testid="pantry-page">
      <div>
        <p className="overline text-[#B3241B]">Remote pantry</p>
        <h1 className="font-display mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
          <Basket size={30} weight="duotone" className="text-[#002FA7]" /> Groceries, wherever you are
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="pantry-explainer">
          Tell us where you live and we find the big stores that trade near you, charge in your own
          money, run it out to {data.hubs[0]?.name || "your hub"} — and once it is in the hub it is
          with you in about {data.promise_minutes} minutes.
        </p>
      </div>

      <div className="border border-border bg-white p-4" data-testid="pantry-locate-box">
        <p className="overline text-slate-500">
          <MapPin size={13} className="mr-1 inline align-[-2px] text-[#B3241B]" /> Where are you?
        </p>
        <div className="mt-2 grid gap-2 sm:grid-cols-[2fr_auto]">
          <Input data-testid="pantry-place" placeholder="Town, postcode or country"
            value={place} onChange={(e) => setPlace(e.target.value)} />
          <Button size="sm" data-testid="pantry-locate" onClick={locate} disabled={busy || place.trim().length < 2}>
            <Sparkle size={14} className="mr-1.5" /> Find my stores
          </Button>
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <select data-testid="pantry-zone" value={zoneKey} aria-label="Delivery zone"
            onChange={(e) => setZoneKey(e.target.value)}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {(data.zones || []).map((z) => (
              <option key={z.key} value={z.key}>{z.country} · {(z.currency || "").toUpperCase()}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Miles hub → home
            <Input data-testid="pantry-miles" type="number" min={0} max={500} value={miles}
              onChange={(e) => setMiles(e.target.value)} className="w-24" />
          </label>
        </div>
        {found && (
          <div className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2" data-testid="pantry-located">
            <p className="text-sm text-[#0F172A]">{found.line}</p>
            <p className="mt-1 text-xs text-slate-600">
              {found.country}{found.region ? ` · ${found.region}` : ""} · stores about{" "}
              {found.miles_to_stores} miles / {found.hours_to_stores} hours away · charged in{" "}
              {(found.zone?.currency || "").toUpperCase()}
            </p>
            <ul className="mt-2 space-y-0.5 text-sm text-slate-700" data-testid="pantry-found-stores">
              {(found.stores || []).map((s, i) => (
                <li key={i}><Storefront size={12} className="mr-1 inline align-[-2px]" />{s.name} · {s.city} · {s.miles} miles</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {(data.offers || []).length > 0 && (
        <div className="border border-[#B3241B] bg-[#FFF5F4] p-4" data-testid="pantry-offers">
          <p className="overline text-[#B3241B]">
            <Tag size={13} className="mr-1 inline align-[-2px]" /> Special deals from the stores
          </p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {data.offers.map((o) => (
              <li key={o.id} data-testid={`pantry-offer-${o.id}`}>
                <span className="font-semibold text-[#0F172A]">{o.title}</span>
                {o.discount_pct ? ` — ${o.discount_pct}% off` : ""}{o.store ? ` at ${o.store}` : " on everything"}
                {o.note ? ` · ${o.note}` : ""}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-slate-500">The best deal you qualify for comes off your receipt automatically.</p>
        </div>
      )}

      <div className="border border-border bg-white p-4" data-testid="pantry-ask">
        <p className="overline text-slate-500">Ask for anything — one per line</p>
        <textarea data-testid="pantry-wanted" rows={5} value={wanted}
          onChange={(e) => setWanted(e.target.value)} aria-label="What you want"
          className="mt-2 w-full border border-border p-3 text-sm" />
        <Button className="mt-2" size="sm" data-testid="pantry-source" onClick={source} disabled={busy}>
          <Sparkle size={14} className="mr-1.5" /> Find it in the stores
        </Button>
      </div>

      {sourced && (
        <div className="border border-[#002FA7] bg-white p-4" data-testid="pantry-sourced">
          <p className="text-sm text-slate-700">{sourced.line}</p>
          <div className="mt-3 space-y-2">
            {sourced.items.map((i, idx) => (
              <div key={idx} className="border border-border p-3" data-testid={`pantry-item-${idx}`}>
                <p className="text-sm font-semibold text-[#0F172A]">{i.item} <span className="font-normal text-slate-500">{i.pack}</span></p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {(i.options || []).map((o, oi) => (
                    <button key={oi} type="button" data-testid={`pantry-option-${idx}-${oi}`}
                      onClick={() => setBasket(basket.map((b, bi) => bi === idx
                        ? { ...b, store: o.store, price: Number(o.price || 0) } : b))}
                      className={`tap-safe border px-3 py-2 text-xs font-semibold ${
                        basket[idx]?.store === o.store ? "border-[#0F172A] bg-[#0F172A] text-white"
                          : "border-border bg-white text-slate-700 hover:border-[#0F172A]"}`}>
                      <Storefront size={12} className="mr-1 inline align-[-2px]" />
                      {o.store} · {sym}{Number(o.price || 0).toFixed(2)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-[2fr_1fr]">
            <Input data-testid="pantry-address" placeholder="Where do we drop it?" value={address}
              onChange={(e) => setAddress(e.target.value)} />
            <select data-testid="pantry-pay" value={pay} onChange={(e) => setPay(e.target.value)}
              aria-label="How you want to pay"
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              <option value="online">Pay online now</option>
              <option value="at_hub">Pay at the hub</option>
            </select>
          </div>

          <div className="mt-3">
            <p className="overline text-slate-500">Tip the hub runner</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {[0, 1, 2, 5].map((t) => (
                <button key={t} type="button" data-testid={`pantry-tip-${t}`}
                  onClick={() => setTip(t)}
                  className={`tap-safe border px-3 py-2 text-xs font-semibold ${
                    Number(tip) === t ? "border-[#0B5C2E] bg-[#0B5C2E] text-white"
                      : "border-border bg-white text-slate-700 hover:border-[#0B5C2E]"}`}>
                  {t === 0 ? "No tip" : `${sym}${t}`}
                </button>
              ))}
              <Input data-testid="pantry-tip-custom" type="number" min={0} value={tip}
                onChange={(e) => setTip(e.target.value)} aria-label="Custom tip" className="w-24" />
            </div>
          </div>

          {ch && (
            <div className="mt-4 border border-border bg-[#FAFAF8] p-3" data-testid="pantry-receipt">
              <p className="overline text-slate-500">
                <Receipt size={13} className="mr-1 inline align-[-2px]" /> Your receipt · {receipt.zone_name}
              </p>
              <dl className="mt-2 space-y-1 text-sm text-slate-700">
                <Row label="Groceries" value={fmt(ch.subtotal)} id="subtotal" />
                {ch.offer_discount > 0 && (
                  <Row label={`Deal — ${ch.offer}`} value={`−${fmt(ch.offer_discount)}`} id="discount" green />
                )}
                <Row label={`Delivery · ${receipt.miles} miles`}
                  value={ch.delivery > 0 ? fmt(ch.delivery) : "Free"} id="delivery" />
                {ch.long_run_fee > 0 && <Row label="Long-run fee" value={fmt(ch.long_run_fee)} id="longrun" />}
                {ch.small_basket_fee > 0 && <Row label="Small basket fee" value={fmt(ch.small_basket_fee)} id="small" />}
                <Row label="Hub handling" value={fmt(ch.hub_fee)} id="hub" />
                <Row label="Service fee" value={fmt(ch.service_fee)} id="service" />
                {ch.tax > 0 && <Row label="Tax" value={fmt(ch.tax)} id="tax" />}
                {ch.tip > 0 && <Row label="Runner tip" value={fmt(ch.tip)} id="tip" />}
              </dl>
              <p className="mt-2 border-t border-border pt-2 text-base font-bold text-[#0F172A]" data-testid="pantry-total">
                Total {fmt(receipt.total)} {(receipt.currency || "").toUpperCase()}
              </p>
              {receipt.free_delivery_over > 0 && ch.delivery > 0 && (
                <p className="mt-1 text-xs text-[#0B5C2E]" data-testid="pantry-free-hint">
                  Spend {fmt(receipt.free_delivery_over)} on groceries and delivery is free.
                </p>
              )}
              <p className="mt-2 text-xs text-slate-500" data-testid="pantry-payouts">
                Of that: store {fmt(receipt.payouts.store)} · hub owner {fmt(receipt.payouts.hub_owner)} ·
                runner {fmt(receipt.payouts.runner)} · us {fmt(receipt.payouts.platform)}
              </p>
            </div>
          )}
          <Button className="mt-3" size="sm" data-testid="pantry-order" onClick={order}
            disabled={busy || !basket.length || !address.trim()}>
            Put it on the run
          </Button>
        </div>
      )}

      <div data-testid="pantry-stock">
        <p className="overline text-slate-500">
          <Timer size={13} className="mr-1 inline align-[-2px] text-[#0B5C2E]" />
          In the hub now · with you in about {data.promise_minutes} minutes
        </p>
        {data.stock.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Nothing landed yet — the next run fills it.</p>
        ) : (
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {data.stock.map((s) => (
              <div key={s.id} className="border border-border bg-white p-3" data-testid={`pantry-stock-${s.id}`}>
                <p className="text-sm font-semibold text-[#0F172A]">{s.item}</p>
                <p className="text-xs text-slate-500">{s.pack} · {s.qty} in · {sym}{Number(s.price || 0).toFixed(2)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div data-testid="pantry-orders">
        <p className="overline text-slate-500">Your orders</p>
        <ul className="mt-2 space-y-2">
          {data.orders.map((o) => (
            <li key={o.id} className="border border-border bg-white p-3" data-testid={`pantry-order-${o.id}`}>
              <p className="text-sm font-semibold text-[#0F172A]">
                {o.reference} · {o.lines.length} lines · {o.symbol || "$"}{Number(o.total).toFixed(2)} {(o.currency || "").toUpperCase()}
              </p>
              {o.charges && (
                <p className="text-xs text-slate-500" data-testid={`pantry-order-charges-${o.id}`}>
                  groceries {o.symbol || "$"}{Number(o.charges.subtotal || 0).toFixed(2)} ·
                  delivery {o.symbol || "$"}{Number(o.charges.delivery || 0).toFixed(2)} ·
                  hub {o.symbol || "$"}{Number(o.charges.hub_fee || 0).toFixed(2)} ·
                  service {o.symbol || "$"}{Number(o.charges.service_fee || 0).toFixed(2)}
                  {o.charges.tip > 0 ? ` · tip ${o.symbol || "$"}${Number(o.charges.tip).toFixed(2)}` : ""}
                </p>
              )}
              <p className="text-xs text-slate-600">
                {o.status.replace(/_/g, " ")} · {o.eta} · {o.payment_status.replace(/_/g, " ")}
              </p>
              {o.status === "in_the_hub" && (
                <Button className="mt-2" size="sm" variant="outline" data-testid={`pantry-deliver-${o.id}`}
                  onClick={async () => {
                    try { await api.post(`/pantry/orders/${o.id}/deliver`, { runner_kind: "crew" });
                      toast.success("Delivered"); load(); } catch (e) { oops(e); }
                  }}>
                  <CheckCircle size={14} className="mr-1.5" /> Mark delivered
                </Button>
              )}
            </li>
          ))}
          {data.orders.length === 0 && <li className="text-sm text-slate-500">No orders yet.</li>}
        </ul>
      </div>

      {data.can_manage && (
        <div className="border border-border bg-white p-4" data-testid="pantry-admin">
          <p className="overline text-slate-500">The run</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="pantry-book-run" disabled={busy}
              onClick={async () => {
                setBusy(true);
                try { await api.post("/pantry/runs", { hub_id: data.hub_id, store_key: data.stores[0].key, cadence: "twice weekly" });
                  toast.success("Run booked"); load(); } catch (e) { oops(e); } finally { setBusy(false); }
              }}>
              <Truck size={14} className="mr-1.5" /> Book the next run
            </Button>
            <Button size="sm" variant="outline" data-testid="pantry-forecast" disabled={busy}
              onClick={async () => {
                try { const { data: f } = await api.get("/pantry/forecast"); setForecast(f); }
                catch (e) { oops(e); }
              }}>
              <Sparkle size={14} className="mr-1.5" /> What should the hub hold?
            </Button>
            <Button size="sm" variant="outline" data-testid="pantry-refresh" disabled={busy}
              onClick={async () => {
                setBusy(true);
                try { await api.post("/pantry/refresh"); toast.success("Board refreshed"); }
                catch (e) { oops(e); } finally { setBusy(false); }
              }}>
              Refresh the public board
            </Button>
          </div>
          <ul className="mt-3 space-y-1 text-sm text-slate-700" data-testid="pantry-runs">
            {runs.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-2">
                <span>{r.store_name} → {r.hub_name} · {r.orders} orders · {r.miles} miles · {r.status}</span>
                {r.status !== "arrived" && (
                  <Button size="sm" data-testid={`pantry-arrive-${r.id}`}
                    onClick={async () => {
                      try { const { data: a } = await api.post(`/pantry/runs/${r.id}/arrive`);
                        toast.success(`${a.orders_landed} order(s) landed in the hub`); load(); }
                      catch (e) { oops(e); }
                    }}>
                    <Plus size={13} className="mr-1" /> It has landed
                  </Button>
                )}
              </li>
            ))}
            {runs.length === 0 && <li className="text-slate-500">No runs booked yet.</li>}
          </ul>
          {forecast && (
            <div className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2" data-testid="pantry-forecast-out">
              <p className="text-sm text-[#0F172A]">{forecast.line}</p>
              <ul className="mt-1 space-y-0.5 text-sm text-slate-700">
                {forecast.hold.slice(0, 8).map((h) => (
                  <li key={h.item}>{h.item} · ordered {h.ordered} · hold {h.hold}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-5 border-t border-border pt-4" data-testid="pantry-rates">
            <p className="overline text-slate-500">Charges for {zone?.country || "this zone"}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" data-testid="pantry-rates-suggest" disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const { data: s } = await api.post("/pantry/zones/suggest", { country: zone?.country || place });
                    setRates({ ...s.rates, _line: s.line, _key: s.key || zone?.key });
                    toast.success("AI has proposed the charges — check them and save");
                  } catch (e) { oops(e); } finally { setBusy(false); }
                }}>
                <Sparkle size={14} className="mr-1.5" /> Suggest the charges
              </Button>
              <Button size="sm" variant="outline" data-testid="pantry-rates-edit"
                onClick={() => setRates(rates ? null : Object.fromEntries(
                  (data.zones.find((z) => z.key === zoneKey) ? Object.entries(data.zones.find((z) => z.key === zoneKey))
                    .filter(([k]) => !["id", "key", "country", "name", "currency", "symbol", "created_at"].includes(k)) : [])))}>
                {rates ? "Close" : "Set them by hand"}
              </Button>
            </div>
            {rates && (
              <div className="mt-3">
                {rates._line && <p className="text-sm text-slate-700">{rates._line}</p>}
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {Object.keys(rates).filter((k) => !k.startsWith("_")).map((k) => (
                    <label key={k} className="text-xs text-slate-600">
                      {k.replace(/_/g, " ")}
                      <Input data-testid={`pantry-rate-${k}`} type="number" value={rates[k]}
                        onChange={(e) => setRates({ ...rates, [k]: e.target.value })} />
                    </label>
                  ))}
                </div>
                <Button className="mt-2" size="sm" data-testid="pantry-rates-save" disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await api.post("/pantry/zones", {
                        key: rates._key || zoneKey, country: zone?.country || "",
                        rates: Object.fromEntries(Object.entries(rates).filter(([k]) => !k.startsWith("_"))),
                      });
                      toast.success("Charges saved"); setRates(null); load();
                    } catch (e) { oops(e); } finally { setBusy(false); }
                  }}>
                  Save the charges
                </Button>
              </div>
            )}
          </div>

          <div className="mt-5 border-t border-border pt-4" data-testid="pantry-offer-admin">
            <p className="overline text-slate-500">
              <Tag size={13} className="mr-1 inline align-[-2px]" /> Put up a store deal
            </p>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <Input data-testid="pantry-offer-title" placeholder="Deal title"
                value={offer.title} onChange={(e) => setOffer({ ...offer, title: e.target.value })} />
              <Input data-testid="pantry-offer-store" placeholder="Store (blank = all)"
                value={offer.store} onChange={(e) => setOffer({ ...offer, store: e.target.value })} />
              <Input data-testid="pantry-offer-pct" type="number" min={0} max={60} aria-label="Percent off"
                value={offer.discount_pct} onChange={(e) => setOffer({ ...offer, discount_pct: e.target.value })} />
            </div>
            <Button className="mt-2" size="sm" data-testid="pantry-offer-save" disabled={busy || offer.title.trim().length < 3}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.post("/pantry/offers", { ...offer, discount_pct: Number(offer.discount_pct) || 0 });
                  toast.success("Deal is live"); setOffer({ store: "", title: "", discount_pct: 10 }); load();
                } catch (e) { oops(e); } finally { setBusy(false); }
              }}>
              Post the deal
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
